/**
 * Single source of truth for the three RAG knowledge scopes.
 * Keeps "which agents receive this document" unambiguous across panel,
 * dropzone, and list. Colors are CSS values (used for low-chroma tints and
 * drag-over states); labels resolve through i18n at the call site.
 */
import type { TFunction } from 'i18next';

export type KnowledgeScope = 'station' | 'manager' | 'device';

export interface ScopeMeta {
  scope: KnowledgeScope;
  /** Heading shown in the panel header. */
  title: string;
  /** Short "who receives this" chip text (audience). */
  audience: string;
  /** Base color for tint + drag-over (CSS color string). */
  color: string;
}

/**
 * Resolve scope from the props a panel was given.
 * device_id absent → station; explicit isManager flag distinguishes the
 * manager's own scope from an employee device scope.
 */
export function resolveScope(opts: {
  deviceId?: string | null;
  isManager?: boolean;
}): KnowledgeScope {
  if (!opts.deviceId) return 'station';
  return opts.isManager ? 'manager' : 'device';
}

/** Accent blue from the design system (matches --color-app-accent). */
const STATION_COLOR = '#0052ff';
/** Manager identity violet (matches manager default in StationManagerSection). */
const MANAGER_COLOR = '#6366f1';

export function scopeMeta(
  scope: KnowledgeScope,
  t: TFunction,
  opts: { deviceColor?: string; deviceCount?: number } = {},
): ScopeMeta {
  switch (scope) {
    case 'station':
      return {
        scope,
        title: t('knowledge.stationTitle', {
          defaultValue: 'เอกสาร RAG สถานี',
        }),
        audience:
          opts.deviceCount != null
            ? t('knowledge.audienceStationCount', {
                defaultValue: 'ทุก device agent · {{count}}',
                count: opts.deviceCount,
              })
            : t('knowledge.audienceStation', {
                defaultValue: 'ทุก device agent',
              }),
        color: STATION_COLOR,
      };
    case 'manager':
      return {
        scope,
        title: t('knowledge.managerTitle', {
          defaultValue: 'เอกสาร RAG (เฉพาะ Manager)',
        }),
        audience: t('knowledge.audienceManager', {
          defaultValue: 'เฉพาะ Manager',
        }),
        color: opts.deviceColor || MANAGER_COLOR,
      };
    case 'device':
    default:
      return {
        scope: 'device',
        title: t('knowledge.deviceTitle', {
          defaultValue: 'เอกสาร RAG (เฉพาะอุปกรณ์นี้)',
        }),
        audience: t('knowledge.audienceDevice', {
          defaultValue: 'เฉพาะอุปกรณ์นี้',
        }),
        color: opts.deviceColor || STATION_COLOR,
      };
  }
}
